import { t, useLanguage } from "@/i18n/LanguageContext";import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Eyebrow, DecorativeStripe } from "@/components/Reveal";
import { ContactLocations } from "@/components/CompanySections";
import { Link } from "react-router-dom";
import { BrochureDownload } from "@/components/BrochureDownload";

export default function Contatti() {useLanguage();
  const { content } = useSite();
  const c = content.contact;
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("Messaggio inviato! Ti ricontatteremo al più presto."));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t(formatApiError(err, "Invio non riuscito. Riprova tra poco.")));
    } finally {
      setSending(false);
    }
  };

  const info = [
  { icon: MapPin, label: "Sede legale", value: c.address, testid: "contact-info-address" },
  { icon: Phone, label: "Telefono", value: c.phone, href: `tel:${c.phone.replace(/[^\d+]/g, "")}`, testid: "contact-info-phone" },
  { icon: Mail, label: "Email", value: c.email, href: `mailto:${c.email}`, testid: "contact-info-email" },
  { icon: FileText, label: "PEC", value: c.pec, href: `mailto:${c.pec}`, testid: "contact-info-pec" },
  { icon: FileText, label: "Codice fiscale e P.IVA", value: c.piva, testid: "contact-info-tax-id" },
  { icon: FileText, label: "Capitale sociale", value: c.capital, testid: "contact-info-capital" },
  { icon: Clock, label: "Orari", value: c.hours, testid: "contact-info-hours" }];


  return (
    <main data-testid="contatti-page">
      <section className="relative bg-[#F8FAFC] pb-16 pt-20 lg:pt-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>{t("Contatti")}</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold tracking-tight text-brand-navydeep sm:text-5xl lg:text-6xl" data-testid="contact-title">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>{t("Parliamo del tuo progetto")}

              </motion.span>
            </span>
          </h1>
        </div>
        <DecorativeStripe testId="contact-header-decorative-stripe" />
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <div className="min-w-0 space-y-4 lg:col-span-2" data-testid="contact-info-panel">
            {t(info.map((item) =>
            <div key={item.label} data-testid={item.testid} className="flex items-start gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-limelight text-brand-navy">
                  <item.icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t(item.label)}</p>
                  <p className="mt-1 break-words text-sm font-semibold text-brand-navydeep">
                    {t(item.href ? <a data-testid={`${item.testid}-link`} href={item.href} className="transition-colors hover:text-brand-navy">{t(item.value)}</a> : item.value)}
                  </p>
                </div>
              </div>
            ))}
            <iframe
              title={t("Mappa della zona di Pozzilli — Strada Statale 85 Venafrana")}
              data-testid="contact-map"
              src={process.env.REACT_APP_CONTACT_MAP_URL}
              className="h-56 w-full rounded-2xl border border-slate-200"
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin" />
            
            <p data-testid="contact-map-note" className="text-xs leading-relaxed text-slate-500">{t("Mappa indicativa della zona di Pozzilli. Per raggiungerci, fai riferimento all’indirizzo completo riportato sopra.")}</p>
          </div>

          <form onSubmit={submit} className="min-w-0 rounded-2xl border border-slate-200/80 bg-[#F8FAFC] p-7 shadow-sm sm:p-10 lg:col-span-3" data-testid="contact-form">
            <h2 className="font-display text-2xl font-bold text-brand-navydeep">{t("Scrivici")}</h2>
            <p className="mt-2 text-sm text-slate-500">{t("Compila il modulo: la richiesta arriva direttamente in azienda via email.")}</p>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="cf-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">{t("Nome e cognome *")}</label>
                <input id="cf-name" data-testid="contact-form-name" required value={form.name} onChange={set("name")}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10" />
              </div>
              <div>
                <label htmlFor="cf-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">{t("Email *")}</label>
                <input id="cf-email" type="email" data-testid="contact-form-email" required value={form.email} onChange={set("email")}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">{t("Telefono")}</label>
                <input id="cf-phone" data-testid="contact-form-phone" value={form.phone} onChange={set("phone")}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="cf-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">{t("Messaggio *")}</label>
                <textarea id="cf-message" rows={5} data-testid="contact-form-message" required value={form.message} onChange={set("message")}
                className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10" />
              </div>
            </div>
            <p data-testid="contact-privacy-notice" className="mt-6 text-xs leading-relaxed text-slate-500">{t("Utilizziamo i tuoi dati per rispondere alla richiesta.")} <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="contact-privacy-link" className="font-semibold text-brand-navy underline">{t("Leggi l’informativa Privacy")}</Link>.</p>
            <button
              type="submit"
              data-testid="contact-form-submit-button"
              disabled={sending}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-navy px-8 py-3.5 font-display text-sm font-bold text-white transition-all duration-300 hover:bg-brand-navydark disabled:opacity-60">
              
              {t(sending ? "Invio in corso…" : "Invia richiesta")} <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </section>
      <BrochureDownload placement="contacts" />
      <ContactLocations />
    </main>);

}
